/**
 * KMZ/KML Parser Utility
 * Parses KMZ (compressed) and KML files to GeoJSON format
 * Preserves folder hierarchy for tree-view selection in project wizard
 *
 * Uses JSZip for extracting KMZ files and @tmcw/togeojson for KML→GeoJSON conversion
 */

import JSZip from 'jszip';
import { kml as toGeoJson } from '@tmcw/togeojson';
import { DOMParser } from '@xmldom/xmldom';

let featureIdCounter = 0;

/**
 * KML/KMZ File Parser Class
 */
export class KmzParser {
  constructor(file) {
    if (!(file instanceof File)) {
      throw new Error('Expected File object');
    }
    this.file = file;
    this.domDocument = null;
    this.isKmz = file.name.toLowerCase().endsWith('.kmz');
  }

  async unzip() {
    const zip = await JSZip.loadAsync(this.file);
    const kmlFile = Object.values(zip.files).find(f =>
      f.name.toLowerCase().endsWith('.kml')
    );
    if (!kmlFile) {
      throw new Error('No KML file found in KMZ archive');
    }
    return await kmlFile.async('text');
  }

  async toString() {
    if (this.isKmz) {
      return await this.unzip();
    }
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsText(this.file);
    });
  }

  async toDom() {
    if (!this.domDocument) {
      const kmlStr = await this.toString();
      this.domDocument = new DOMParser().parseFromString(kmlStr, 'text/xml');
    }
    return this.domDocument;
  }

  async toGeoJson() {
    const dom = await this.toDom();
    return toGeoJson(dom, { styles: true });
  }

  async getPolygons() {
    const geoJson = await this.toGeoJson();
    return geoJson.features.filter(f =>
      f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon'
    );
  }

  async getNamedFeatures() {
    const geoJson = await this.toGeoJson();
    return geoJson.features.map((feature, index) => {
      // description may be an object { @type, value } from togeojson -- extract string
      let desc = feature.properties?.description || '';
      if (desc && typeof desc === 'object') {
        desc = desc.value || '';
      }
      // Strip HTML tags for display
      if (typeof desc === 'string' && desc.startsWith('<')) {
        desc = desc.replace(/<[^>]*>/g, '').trim().substring(0, 200);
      }
      return {
        id: `feature-${index}`,
        name: String(feature.properties?.name || `Unnamed ${index + 1}`),
        description: String(desc),
        geometry: feature.geometry,
        geometryType: feature.geometry?.type || 'Unknown',
        properties: feature.properties,
      };
    });
  }
}

/**
 * Walk a KML DOM node and build a tree structure preserving folders.
 * Each node: { id, name, type: 'folder'|'placemark', children, geometry?, geometryType? }
 */
function walkKmlNode(node, parentPath = '') {
  const children = [];
  const childNodes = node.childNodes || [];

  for (let i = 0; i < childNodes.length; i++) {
    const child = childNodes[i];

    if (child.nodeName === 'Folder') {
      const nameEl = getDirectChild(child, 'name');
      const folderName = nameEl?.textContent?.trim() || 'Unnamed Folder';
      const folderId = `folder-${parentPath}${folderName}`.replace(/\s+/g, '_');

      const folderChildren = walkKmlNode(child, `${folderId}/`);

      children.push({
        id: folderId,
        name: folderName,
        type: 'folder',
        children: folderChildren,
        placemarkCount: countPlacemarks(folderChildren),
      });
    } else if (child.nodeName === 'Placemark') {
      const nameEl = getDirectChild(child, 'name');
      const pName = nameEl?.textContent?.trim() || `Placemark ${++featureIdCounter}`;
      const pId = `pm-${featureIdCounter++}-${parentPath}${pName}`.replace(/\s+/g, '_');

      // Extract geometry type
      let geometryType = null;
      let geometry = null;
      for (const geoType of ['Polygon', 'MultiGeometry', 'LineString', 'Point']) {
        const geoEl = child.getElementsByTagName(geoType);
        if (geoEl.length > 0) {
          geometryType = geoType;
          break;
        }
      }

      children.push({
        id: pId,
        name: pName,
        type: 'placemark',
        geometryType,
        children: [],
        placemarkCount: 1,
      });
    }
  }

  return children;
}

function getDirectChild(node, tagName) {
  const children = node.childNodes || [];
  for (let i = 0; i < children.length; i++) {
    if (children[i].nodeName === tagName) return children[i];
  }
  return null;
}

function countPlacemarks(nodes) {
  let count = 0;
  for (const n of nodes) {
    if (n.type === 'placemark') count++;
    else if (n.children) count += countPlacemarks(n.children);
  }
  return count;
}

/**
 * Collect all leaf placemark IDs under a tree node (for selecting a folder = select all children)
 */
export function collectPlacemarkIds(node) {
  const ids = [];
  if (node.type === 'placemark') {
    ids.push(node.id);
  }
  if (node.children) {
    for (const child of node.children) {
      ids.push(...collectPlacemarkIds(child));
    }
  }
  return ids;
}

/**
 * Flatten the tree to a list of all nodes with depth
 */
export function flattenTree(nodes, depth = 0) {
  const result = [];
  for (const node of nodes) {
    result.push({ ...node, depth });
    if (node.children?.length > 0) {
      result.push(...flattenTree(node.children, depth + 1));
    }
  }
  return result;
}

/**
 * Parse a KMZ/KML file and return features + folder tree
 * @param {File} file - The file to parse
 * @returns {Object} Object with features, tree, and metadata
 */
export async function parseKmzFile(file) {
  featureIdCounter = 0;
  const parser = new KmzParser(file);

  const [features, geoJson, dom] = await Promise.all([
    parser.getNamedFeatures(),
    parser.toGeoJson(),
    parser.toDom(),
  ]);

  // Build folder tree from DOM
  const documentEl = dom.getElementsByTagName('Document')[0] || dom.documentElement;
  const tree = walkKmlNode(documentEl);

  return {
    fileName: file.name,
    isKmz: parser.isKmz,
    featureCount: features.length,
    features,
    geoJson,
    tree,
    polygons: features.filter(f =>
      f.geometryType === 'Polygon' || f.geometryType === 'MultiPolygon'
    ),
    lines: features.filter(f =>
      f.geometryType === 'LineString' || f.geometryType === 'MultiLineString'
    ),
    points: features.filter(f => f.geometryType === 'Point'),
  };
}

/**
 * Extract boundaries from a GeoJSON feature
 */
export function extractBoundaryCoordinates(feature) {
  if (!feature?.geometry?.coordinates) return [];

  const { geometry } = feature;

  if (geometry.type === 'Polygon') {
    return geometry.coordinates[0];
  }

  if (geometry.type === 'MultiPolygon') {
    return geometry.coordinates[0][0];
  }

  return [];
}

/**
 * Convert coordinates to GeoJSON polygon string
 */
export function coordinatesToGeoJsonString(coordinates) {
  const geoJson = {
    type: 'Polygon',
    coordinates: [coordinates],
  };
  return JSON.stringify(geoJson);
}

export default KmzParser;
